// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// Feature 096 — the process navigation. The 089 switcher's list, moved out
// of the header into the shell's sidebar: the engagement and activity at
// the head with the way back, the two views (Business map, Process
// landscape), a search box, and every live process grouped under its
// business-map domain in storm order. New process opens a small dialog;
// rename, reorder, remove and restore live behind "Manage processes",
// which hosts the switcher itself, so the everyday list stays quiet.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, Map as MapIcon } from 'lucide-react';
import ProcessSwitcher, { type ProcessSwitcherEntry } from './ProcessSwitcher';
import ShellDialog from './ShellDialog';
import {
  groupByDomain,
  stateLabel,
  stormNextId,
  type LandscapeDomain,
} from '../utils/processLandscape';
import '../styles/ProcessNavigation.css';

export interface ProcessNavigationEntry extends ProcessSwitcherEntry {
  order: number;
  // 095: the business map's importance and storm rank; 096: its domain.
  importance?: 'core' | 'supporting' | 'generic' | null;
  rank?: number | null;
  domainId?: string | null;
}

export interface ProcessNavigationProps {
  engagementLabel: string;
  activityName: string;
  backTo: string;
  businessMapTo: string | null;
  processes: ProcessNavigationEntry[];
  domains: LandscapeDomain[];
  focusedProcessId: string | null;
  landscapeActive: boolean;
  onLandscape: () => void;
  onSelect: (processId: string) => void;
  onCreate: (name: string) => void;
  onRename: (processId: string, name: string) => void;
  onRemove: (processId: string) => void;
  onRestore: (processId: string) => void;
  onReorder: (processId: string, direction: 'up' | 'down') => void;
  backLabel?: string;
}

function rankLabel(rank: number | null | undefined): string {
  return rank == null ? '' : String(rank).padStart(2, '0');
}

export default function ProcessNavigation({
  engagementLabel, activityName, backTo, businessMapTo, processes, domains, focusedProcessId,
  landscapeActive, onLandscape, onSelect, onCreate, onRename, onRemove, onRestore, onReorder, backLabel = 'Back to activity',
}: ProcessNavigationProps) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [managing, setManaging] = useState(false);

  const live = useMemo(() => processes.filter((process) => !process.removedAt), [processes]);
  const stormNext = useMemo(() => stormNextId(live), [live]);
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle ? live.filter((process) => process.name.toLowerCase().includes(needle)) : live;
    return groupByDomain(matching, domains);
  }, [live, domains, query]);

  const submitCreate = () => {
    const name = createName.trim();
    if (!name) return;
    setCreating(false);
    setCreateName('');
    onCreate(name);
  };

  return (
    <nav className="pnav" aria-label="Process navigation">
      <div className="pnav-head">
        <div className="pnav-eyebrow">{engagementLabel}</div>
        <strong className="pnav-activity">{activityName}</strong>
        <Link className="pnav-back" to={backTo}>{backLabel}</Link>
      </div>

      <div className="pnav-body">
        {businessMapTo ? (
          <Link className="pnav-view" to={businessMapTo}>
            <LayoutGrid size={15} aria-hidden="true" />
            <span>Business map</span>
          </Link>
        ) : null}
        <button
          type="button"
          className={`pnav-view${landscapeActive ? ' is-active' : ''}`}
          aria-pressed={landscapeActive}
          onClick={onLandscape}
        >
          <MapIcon size={15} aria-hidden="true" />
          <span>Process landscape</span>
          <span className="pnav-count">{live.length}</span>
        </button>

        <input
          className="pnav-search"
          type="search"
          aria-label="Search processes"
          placeholder="Search processes"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <nav className="pnav-list" aria-label="Processes">
          {groups.length === 0 ? (
            <p className="pnav-empty">{live.length === 0 ? 'No processes yet.' : 'No matching processes.'}</p>
          ) : groups.map((group) => (
            <section key={group.id} className="pnav-group">
              <h3 className="pnav-group-name">{group.name}</h3>
              <ul>
                {group.processes.map((process) => {
                  const active = process.processId === focusedProcessId;
                  const next = process.processId === stormNext;
                  const meta = [
                    ...(next ? ['Storm next'] : []),
                    ...(process.importance ? [process.importance] : []),
                    stateLabel(process.state),
                  ].join(' · ');
                  return (
                    <li key={process.processId}>
                      <button
                        type="button"
                        className={`pnav-row${active ? ' is-active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => onSelect(process.processId)}
                      >
                        <span className="pnav-rank" aria-hidden="true">{rankLabel(process.rank)}</span>
                        <span className="pnav-row-body">
                          <span className="pnav-row-name">{process.name}</span>
                          <small className={`pnav-row-meta${next ? ' is-next' : ''}`}>{meta}</small>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </nav>
      </div>

      <div className="pnav-foot">
        <button type="button" className="pnav-foot-btn pnav-foot-btn--primary" onClick={() => { setCreating(true); setCreateName(''); }}>
          New process
        </button>
        <button type="button" className="pnav-foot-btn" onClick={() => setManaging(true)}>
          Manage processes
        </button>
      </div>

      {creating ? (
        <ShellDialog eyebrow="Process register" title="Create a process" onClose={() => setCreating(false)}>
          <p>Start with a name. Place it in a business domain on the Business map later.</p>
          <input
            className="shell-dialog-input"
            aria-label="Process name"
            placeholder="For example, Resolve delivery exception"
            autoFocus
            maxLength={80}
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submitCreate();
            }}
          />
          <div className="shell-dialog-actions">
            <button type="button" className="shell-dialog-btn" onClick={() => setCreating(false)}>Cancel</button>
            <button type="button" className="shell-dialog-btn shell-dialog-btn--primary" disabled={!createName.trim()} onClick={submitCreate}>
              Create and open
            </button>
          </div>
        </ShellDialog>
      ) : null}

      {managing ? (
        <ShellDialog eyebrow="Process register" title="Manage processes" onClose={() => setManaging(false)} className="pnav-manage">
          <p>Rename, reorder, remove or restore. The storm order comes from the Business map.</p>
          <ProcessSwitcher
            processes={processes}
            focusedProcessId={focusedProcessId}
            onSelect={(processId) => { setManaging(false); onSelect(processId); }}
            onCreate={(name) => { setManaging(false); onCreate(name); }}
            onRename={onRename}
            onRemove={onRemove}
            onRestore={onRestore}
            onReorder={onReorder}
          />
        </ShellDialog>
      ) : null}
    </nav>
  );
}
